import {
  DEFAULTS,
  DIVERSITY_BONUS_VALUE,
  LEARNING_FIRST_ATTEMPT_P_CORRECT,
  LEARNING_GAIN_CENTER,
  LEARNING_GAIN_SIGMA,
  MODE_CONFIG,
  MORALE_FIT_SIGMA,
  NEW_ITEM_P_CORRECT,
  NOVELTY_BONUS_VALUE,
  PREDICTOR_COEFFICIENTS,
  RETRY_BONUS_VALUE,
  SCORING_WEIGHTS,
} from "./constants";
import type {
  CandidateState,
  PresentedType,
  ScoredCandidate,
  SessionItem,
  SessionMode,
} from "./types";

function sigmoid(x: number): number {
  return 1 / (1 + Math.exp(-x));
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function hoursBetween(now: Date, then: Date | null): number {
  if (!then) return Infinity;
  return Math.max(0, (now.getTime() - then.getTime()) / (1000 * 60 * 60));
}

function minutesBetween(now: Date, then: Date | null): number {
  if (!then) return Infinity;
  return Math.max(0, (now.getTime() - then.getTime()) / (1000 * 60));
}

/**
 * Calculate the predicted probability of correct answer for a problem
 */
export function calculatePCorrect(state: CandidateState, now: Date): number {
  // New items have fixed p_correct
  if (state.status === "new") {
    return NEW_ITEM_P_CORRECT;
  }

  // Learning items with no successes
  if (state.status === "learning" && state.successCount === 0) {
    return LEARNING_FIRST_ATTEMPT_P_CORRECT;
  }

  const deltaHours = hoursBetween(now, state.lastAnsweredAt);

  // For seen items, calculate raw score
  const recentFailureFlag = state.lastResult === "wrong" ? 1 : 0;
  const sameDayRepeatPenalty = Math.min(1, state.seenCountToday / 3);
  const speedPenalty =
    state.recentAvgResponseMs != null
      ? clamp((state.recentAvgResponseMs - 4000) / 6000, 0, 1)
      : 0;

  const rawScore =
    PREDICTOR_COEFFICIENTS.memoryStrength * state.memoryStrength +
    PREDICTOR_COEFFICIENTS.difficulty * state.difficulty +
    PREDICTOR_COEFFICIENTS.timeFactor * Math.log(1 + deltaHours) +
    PREDICTOR_COEFFICIENTS.consecutiveCorrect *
      Math.min(state.consecutiveCorrect, 5) +
    PREDICTOR_COEFFICIENTS.recentFailure * recentFailureFlag +
    PREDICTOR_COEFFICIENTS.sameDayRepeat * sameDayRepeatPenalty +
    PREDICTOR_COEFFICIENTS.speedPenalty * speedPenalty;

  return sigmoid(rawScore);
}

/**
 * Calculate urgency score - rises when item is overdue or recall is low
 */
export function calculateUrgency(
  pCorrect: number,
  dueAt: Date | null,
  now: Date
): number {
  let hoursOverdue = 0;
  if (dueAt && dueAt < now) {
    hoursOverdue = hoursBetween(now, dueAt);
  }

  const overdueFactor = 1 - Math.exp(-hoursOverdue / 24);

  return 0.6 * (1 - pCorrect) + 0.4 * overdueFactor;
}

/**
 * Calculate learning gain - peaks for medium-difficulty items
 */
export function calculateLearningGain(pCorrect: number): number {
  const diff = pCorrect - LEARNING_GAIN_CENTER;
  return Math.exp(-(diff * diff) / (2 * LEARNING_GAIN_SIGMA * LEARNING_GAIN_SIGMA));
}

/**
 * Calculate frustration risk - rises sharply for low p_correct
 */
export function calculateFrustrationRisk(pCorrect: number): number {
  const raw = (0.55 - pCorrect) / 0.55;
  return Math.pow(clamp(raw, 0, 1), 2);
}

/**
 * Calculate morale fit - how close to target accuracy
 */
export function calculateMoraleFit(
  pCorrect: number,
  targetAccuracy: number
): number {
  const diff = pCorrect - targetAccuracy;
  return Math.exp(-(diff * diff) / (2 * MORALE_FIT_SIGMA * MORALE_FIT_SIGMA));
}

/**
 * Calculate diversity bonus based on previous problem's group
 */
export function calculateDiversityBonus(
  groupId: string,
  previousGroupId: string | null
): number {
  if (!previousGroupId) return 0;
  return groupId !== previousGroupId ? DIVERSITY_BONUS_VALUE : 0;
}

/**
 * Calculate retry bonus for recently missed items
 */
export function calculateRetryBonus(
  state: CandidateState,
  pCorrect: number,
  minPCorrect: number,
  now: Date
): number {
  if (state.lastResult !== "wrong") return 0;

  const minutesSince = minutesBetween(now, state.lastAnsweredAt);
  if (minutesSince < DEFAULTS.retryDelayMinutes) return 0;

  if (pCorrect < minPCorrect + 0.1) return 0;

  return RETRY_BONUS_VALUE;
}

/**
 * Calculate novelty bonus for new items
 */
export function calculateNoveltyBonus(status: string): number {
  return status === "new" ? NOVELTY_BONUS_VALUE : 0;
}

/**
 * Score a single candidate problem
 */
export function scoreCandidate(
  problemId: string,
  groupId: string,
  state: CandidateState,
  mode: SessionMode,
  now: Date,
  previousGroupId: string | null,
  poolType: PresentedType
): ScoredCandidate {
  const config = MODE_CONFIG[mode];

  const pCorrect = calculatePCorrect(state, now);
  const urgency = calculateUrgency(pCorrect, state.dueAt, now);
  const learningGain = calculateLearningGain(pCorrect);
  const frustrationRisk = calculateFrustrationRisk(pCorrect);
  const moraleFit = calculateMoraleFit(pCorrect, config.targetAccuracy);
  const diversityBonus = calculateDiversityBonus(groupId, previousGroupId);
  const retryBonus = calculateRetryBonus(
    state,
    pCorrect,
    config.minPCorrect,
    now
  );
  const noveltyBonus = calculateNoveltyBonus(state.status);

  const finalScore =
    SCORING_WEIGHTS.urgency * urgency +
    SCORING_WEIGHTS.learningGain * learningGain +
    SCORING_WEIGHTS.moraleFit * moraleFit +
    SCORING_WEIGHTS.diversityBonus * diversityBonus -
    SCORING_WEIGHTS.frustrationRisk * frustrationRisk +
    retryBonus +
    noveltyBonus;

  return {
    problemId,
    groupId,
    pCorrect,
    urgency,
    learningGain,
    frustrationRisk,
    moraleFit,
    diversityBonus,
    retryBonus,
    noveltyBonus,
    finalScore,
    poolType,
    state,
  };
}

/**
 * Select the best candidate that optimizes score while maintaining target accuracy
 */
export function selectBestCandidate(
  queue: SessionItem[],
  candidates: ScoredCandidate[],
  targetAccuracy: number
): ScoredCandidate {
  if (candidates.length === 0) {
    throw new Error("No candidates available");
  }

  let best = candidates[0];
  let bestValue = -Infinity;

  const existingSum = queue.reduce((sum, item) => sum + item.pCorrect, 0);
  const existingCount = queue.length;

  for (const candidate of candidates) {
    const projectedAccuracy =
      (existingSum + candidate.pCorrect) / (existingCount + 1);

    const targetPenalty = Math.abs(projectedAccuracy - targetAccuracy);
    const selectionValue =
      candidate.finalScore - SCORING_WEIGHTS.targetPenalty * targetPenalty;

    if (selectionValue > bestValue) {
      bestValue = selectionValue;
      best = candidate;
    }
  }

  return best;
}

/**
 * Calculate expected accuracy for a session queue
 */
export function calculateExpectedAccuracy(items: SessionItem[]): number {
  if (items.length === 0) return 0;
  const sum = items.reduce((acc, item) => acc + item.pCorrect, 0);
  return sum / items.length;
}

/**
 * Calculate dynamic target adjustment based on recent performance
 */
export function calculateAdjustedTarget(
  baseTarget: number,
  recentAccuracy: number
): number {
  if (recentAccuracy <= baseTarget - 0.25) {
    return Math.min(0.92, baseTarget + 0.08);
  } else if (recentAccuracy >= baseTarget + 0.12) {
    return Math.max(0.6, baseTarget - 0.05);
  }
  return baseTarget;
}
